import Head from 'next/head';
import React from 'react';

import Heading from '@jpp/atoms/Heading';
import RefImage from '@jpp/atoms/Image';
import AcfComponents from '@jpp/organisms/AcfComponents/AcfComponents';
import BackTo from '@jpp/organisms/BackTo';
import RelatedPosts from '@jpp/organisms/RelatedPosts';
import Container from '@jpp/shared/Grid/Container';
import Flex from '@jpp/shared/Grid/Flex';
import Row from '@jpp/shared/Grid/Row';
import Section from '@jpp/shared/Grid/Section';
import { WYSIWYG } from '@jpp/shared/WYSIWYG/WYSIWYG';
import { EPageType, ETheme } from 'common/typings/enums';
import { IBlogPostTemplateProps } from 'common/typings/types';

import './Post.scss';

const ROOT_CLASSNAME = 'Post';

const Post: React.FunctionComponent<IBlogPostTemplateProps> = ({ page }) => {
  const {
    post_title,
    content,
    category_names,
    acf_fields,
    author,
    media,
    featured_image,
  } = page;
  const acf = page.acf || acf_fields;
  const title = post_title || page.title;
  const category = category_names ? category_names[0] : undefined;
  const subheading = acf?.subtitle;
  const imageSrc = media?.large ?? featured_image?.url;
  const date = new Date(page.date).toLocaleString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const lastUpdated = new Date(
    page?.date_modified ?? (page as any)?.modified
  ).toLocaleString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const meta = `${author} <span class="${ROOT_CLASSNAME}__meta-dot">•</span> ${date}`;
  const postMetaData =
    lastUpdated !== date
      ? `${meta} <span class="${ROOT_CLASSNAME}__last-updated">(Last updated - ${lastUpdated})</span>`
      : meta;

  console.log({
    lastUpdated,
    date,
  });

  return (
    <>
      {imageSrc && (
        <Head>
          <meta key="og:image" property="og:image" content={imageSrc} />
        </Head>
      )}

      <BackTo href={`/${EPageType.Blog}`} label="all blog posts" />

      <Section className={ROOT_CLASSNAME}>
        <Container>
          <Row className={`${ROOT_CLASSNAME}__row`}>
            <Flex col={12} className={`${ROOT_CLASSNAME}__header`}>
              <Heading
                priority={1}
                className={`${ROOT_CLASSNAME}__heading`}
                children={title}
              />

              {subheading && (
                <Heading
                  priority={2}
                  className={`${ROOT_CLASSNAME}__subheading`}
                  children={subheading}
                />
              )}

              {category && (
                <Heading
                  priority={3}
                  className={`${ROOT_CLASSNAME}__category`}
                  children={category}
                />
              )}

              {postMetaData && (
                <Heading
                  priority={4}
                  className={`${ROOT_CLASSNAME}__meta`}
                  children={postMetaData}
                />
              )}

              <hr className={`${ROOT_CLASSNAME}__divider`} />
            </Flex>
          </Row>

          <Row className={`${ROOT_CLASSNAME}__row`}>
            <Flex col={12} className="mx-auto">
              {imageSrc && (
                <div className={`${ROOT_CLASSNAME}__image-wrapper`}>
                  <RefImage
                    className={`${ROOT_CLASSNAME}__image`}
                    image={
                      { url: imageSrc, alt: `${title} image` } as Core.IMedia
                    }
                    shouldLazyLoad={false}
                  />
                </div>
              )}
            </Flex>

            <Flex
              colLg={7}
              colMd={7}
              colSm={6}
              className={`${ROOT_CLASSNAME}__content-wrapper`}
            >
              <WYSIWYG
                className={`${ROOT_CLASSNAME}__content`}
                content={content}
              />
            </Flex>

            <Flex
              colLg={5}
              colMd={5}
              colSm={6}
              className={`${ROOT_CLASSNAME}__related-posts-wrapper`}
            >
              <RelatedPosts className={`${ROOT_CLASSNAME}__related-posts`} />
            </Flex>
          </Row>
        </Container>
      </Section>

      {acf && acf.components && (
        <AcfComponents
          components={acf?.components}
          page_theme={ETheme.TintAlpha}
        />
      )}
    </>
  );
};

export default Post;
